import React, { useRef, useEffect } from "react";
import { Printer } from "lucide-react";
import Lottie, { LottieRefCurrentProps } from "lottie-react";

import printingAnimation from "../../../assets/animations/printing/printing-start.json";
import { useQzTrayPrint } from "../../../hooks/qztray/useQzTrayPrint";
import { useQzTrayPrinter } from "../../../hooks/qztray/useQzTrayPrinter";
import { useQzTrayClient } from "../../../hooks/qztray/useQzTrayClient";

import TicketInfoCard from "./TicketInfoCard";
import TicketActionSection from "./TicketActionSection";

const TicketRightPanel: React.FC = () => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const { payload, permission, closePrintModal, resetPrintState } = useQzTrayPrint();
  const { printFromPayload, isPrinting } = useQzTrayPrinter();
  const { selectedPrinter, setPrinter } = useQzTrayClient();

  /**
   * 🖨️ Seleccionar automáticamente la impresora *por sucursal*
   * SIEMPRE priorizamos la del backend.
   */
  useEffect(() => {
    if (payload?.printer) {
      setPrinter(payload.printer);     // ⚡ Forzar impresora correcta por sucursal
    }
  }, [payload?.printer, setPrinter]);

  /**
   * Valores calculados
   */
  const printerName = payload?.printer || selectedPrinter || "—";
  const printerType = payload?.type ?? "Térmica";
  const createdAt = payload?.metadata?.createdAt ?? "—";
  const referenceId =
    payload?.metadata?.referenceId ?? permission.id?.toString() ?? "—";

  /**
   * 🎬 Animación mientras imprime
   */
  useEffect(() => {
    if (isPrinting) {
      lottieRef.current?.setSpeed(1);
      lottieRef.current?.goToAndPlay(0, true);
    } else {
      lottieRef.current?.stop();
    }
  }, [isPrinting]);

  /**
   * Acción: Confirmar impresión
   */
  const handleConfirm = async () => {
    await printFromPayload(); // el hook ya maneja cierre y limpieza
  };

  /**
   * Acción: Cancelar
   */
  const handleCancel = () => {
    closePrintModal();
    resetPrintState();
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 md:p-5">
      <div className="w-full">
        <TicketInfoCard
          icon={Printer}
          title="Detalles de impresión"
          subtitle="Información del dispositivo y documento"
          printerName={printerName}
          printerType={printerType}
          createdAt={createdAt}
          referenceId={referenceId}
          receiver={payload?.receiver ?? payload?.metadata?.receiver}
          documentContext={permission.type === 'PURCHASE' ? 'PURCHASE' : 'SALE'}
        />
      </div>

      <div className="mt-4 flex h-[110px] w-[110px] items-center justify-center">
        <Lottie
          lottieRef={lottieRef}
          animationData={printingAnimation}
          loop
          autoplay={false}
          style={{ width: 110, height: 110 }}
        />
      </div>

      <div className="mt-3 w-full">
        <TicketActionSection
          isPrinting={isPrinting}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default TicketRightPanel;
